import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Tag, Images, CalendarPlus, MessagesSquare, User, Bell, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/atoms";
import api, { fileUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Offers", icon: Tag, end: true },
  { to: "/gallery", label: "Gallery", icon: Images },
  { to: "/bookings", label: "Book", icon: CalendarPlus },
  { to: "/chat", label: "Chat", icon: MessagesSquare },
  { to: "/profile", label: "Profile", icon: User },
];

const MORE = [
  { to: "/flash", label: "Flash" },
  { to: "/artists", label: "Artists" },
  { to: "/reviews", label: "Reviews" },
  { to: "/messages", label: "Messages" },
  { to: "/membership", label: "Membership" },
];

export default function Shell({ children }) {
  const { user, branding } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    let t;
    const poll = async () => {
      if (!isAdmin) return;
      try {
        const { data } = await api.get("/admin/notifications");
        setUnread(data.filter((n) => !n.read).length);
      } catch (e) {}
    };
    poll();
    t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [isAdmin]);

  return (
    <div className="phone-wrap">
      <div className="phone-frame flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-[#8B1E28] border-b-2 border-black">
          <div className="flex items-center justify-between px-4 h-14">
            <button className="flex items-center gap-2" onClick={() => navigate("/")} data-testid="topbar-home">
              <img src={fileUrl(branding?.profile_logo)} alt="ATC" className="w-9 h-9 rounded-full border-2 border-black bg-[#F4F0E6] object-cover" />
              <span className="font-display font-extrabold text-[#F4F0E6] uppercase tracking-wide text-sm leading-none">Alpha<br/>Tattoo Club</span>
            </button>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <button className="relative" onClick={() => navigate("/admin")} data-testid="topbar-bell">
                  <Bell size={22} color="#F4F0E6" />
                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#C7A656] text-black text-[10px] font-mono2 font-bold w-4 h-4 rounded-full flex items-center justify-center border border-black">
                      {unread}
                    </span>
                  )}
                </button>
              )}
              <button onClick={() => setMenuOpen(true)} data-testid="topbar-menu">
                <Menu size={24} color="#F4F0E6" />
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 pb-24">{children}</main>

        {/* Bottom nav */}
        <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[440px] bg-[#EAE4D3] border-t-2 border-black z-40">
          <div className="grid grid-cols-5">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-1 py-2 min-h-[60px] ${isActive ? "bg-[#8B1E28] text-[#F4F0E6]" : "text-[#121212]"}`
                  }
                >
                  <Icon size={20} />
                  <span className="font-mono2 text-[10px] uppercase tracking-wider">{n.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Slide-over menu */}
        {menuOpen && (
          <div className="fixed inset-0 z-50 bg-black/70" onClick={() => setMenuOpen(false)} data-testid="menu-overlay">
            <div
              className="absolute right-0 top-0 h-full w-72 max-w-[80%] bg-[#F4F0E6] border-l-2 border-black p-5 rise-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="eyebrow">The Club</span>
                <button onClick={() => setMenuOpen(false)} data-testid="menu-close"><X size={22} /></button>
              </div>
              {user && (
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-black">
                  <Avatar src={user.picture} name={user.username || user.name} size={46} admin={isAdmin} />
                  <div className="min-w-0">
                    <p className="font-display font-bold truncate">{user.username || user.name}</p>
                    <p className="text-xs text-[#554E40] truncate">{user.email}</p>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-1">
                {MORE.map((m) => (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`menu-${m.label.toLowerCase()}`}
                    className="font-display font-bold uppercase tracking-wide py-3 border-b border-black/30"
                  >
                    {m.label}
                  </NavLink>
                ))}
                {isAdmin && (
                  <NavLink to="/admin" onClick={() => setMenuOpen(false)} data-testid="menu-admin" className="font-display font-bold uppercase tracking-wide py-3 border-b border-black/30 text-[#8B1E28]">
                    Admin Console
                  </NavLink>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
